import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Component, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PrecastService } from 'src/app/shared/services/precast.service';
import { UserService } from 'src/app/shared/services/user.service';

@Component({
    selector: 'app-preview-attachments-view',
    templateUrl: './preview-attachments-view.component.html',
    styleUrls: ['./preview-attachments-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PreviewAttachmentsViewComponent {
  projectId: string = '';
  currentFileName: string = '';
  previewList: { fileName: string }[] = [];
  currentIndex: number = 0;
  currentFileUrl: string = '';
  currentTextContent: string = '';

  constructor(
    public precastService: PrecastService,
    private route: ActivatedRoute,
    public userService: UserService,
  ) {}

  ngOnInit() {
    this.projectId = this.route.snapshot.paramMap.get('projectId') || '';
    this.currentFileName = this.route.snapshot.paramMap.get('fileName') || '';

    if (!this.projectId) return;

    this.precastService.getAllattachments(this.projectId, false).subscribe({
      next: (response) => {
        this.previewList = response || [];

        if (this.currentFileName) {
          this.currentIndex = this.previewList.findIndex(f => f.fileName === this.currentFileName);
          if (this.currentIndex === -1) this.currentIndex = 0;
        }

        this.loadFile(this.previewList[this.currentIndex].fileName);
      }
    });
  }

  loadFile(fileName: string) {
    this.precastService.getAttachement(this.projectId, fileName).subscribe({
      next: (response) => {
        const fileUrl = response.url;
        this.currentFileUrl = fileUrl;
        this.currentTextContent = '';

        if (this.isText(fileName)) {
          fetch(fileUrl)
            .then(res => res.text())
            .then(text => this.currentTextContent = text)
            .catch(err => console.error('Error loading text file', err));
        }
      }
    });
  }

  isImage(fileName: string): boolean {
    const ext = fileName.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext || '');
  }

  isPdf(fileName: string): boolean {
    const ext = fileName.split('.').pop()?.toLowerCase();
    return ext === 'pdf';
  }

  isText(fileName: string): boolean {
    const ext = fileName.split('.').pop()?.toLowerCase();
    return ext === 'txt' || ext === 'db' || ext === fileName.toLocaleLowerCase();
  }

  nextFile() {
    if (this.currentIndex < this.previewList.length - 1) {
      this.currentIndex++;
      this.loadFile(this.previewList[this.currentIndex].fileName);
      this.currentFileName = this.previewList[this.currentIndex].fileName;
    }
  }

  prevFile() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.loadFile(this.previewList[this.currentIndex].fileName);
      this.currentFileName = this.previewList[this.currentIndex].fileName;
    }
  }

  downloadCurrentFile() {
    if (this.currentFileUrl) {
      fetch(this.currentFileUrl)
        .then(response => response.blob())
        .then(blob => {
          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = this.currentFileName || 'download';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        })
        .catch(error => console.error('Download failed', error));
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'ArrowLeft') {
      this.prevFile();
    } else if (event.key === 'ArrowRight') {
      this.nextFile();
    }
  }
}
